import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { UUID_REGEX } from '../utils/slug';
import { useAuth } from '../context/AuthContext';
import RsvpButton from '../components/RsvpButton';
import FollowButton from '../components/FollowButton';
import { CARTO_ATTRIBUTION, CARTO_TILE_URL } from '../utils/basemap';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import QRCode from 'react-qr-code';
import 'leaflet/dist/leaflet.css';

type Club = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  hero_image?: string | null;
  logo?: string | null;
  instagram_handle?: string | null;
  facebook_link?: string | null;
  strava_link?: string | null;
  leader_id?: string | null;
};

type Run = {
  id: string;
  club_id: string;
  title?: string | null;
  is_recurring?: boolean | null;
  day_of_week: string;
  run_date?: string | null;
  start_time?: string | null;
  location_address?: string | null;
  distance?: string | null;
  vibes?: string[] | null;
  notes?: string | null;
  route_id?: string | null;
  route_url?: string | null;
  last_confirmed_at?: string | null;
};

// An exception to a standing weekly slot on one date (see run_instances).
type RunException = {
  run_id: string;
  run_date: string;
  status: 'canceled' | 'moved' | 'special';
  title: string | null;
  start_time_override: string | null;
  location_address_override: string | null;
  distance_override: string | null;
  notes_override: string | null;
  route_id: string | null;
};

type RouteRecord = {
  id: string;
  name: string;
  external_url?: string | null;
};

type NextRunResult = {
  run: Run;
  nextDate: Date;
};

type RunLeader = {
  id: string;
  user_id?: string | null;
  name: string;
  // not returned by get_run_leader_profiles — leader emails are not public
  email?: string;
  profile_image?: string | null;
  created_at?: string | null;
  is_admin?: boolean | null;
};

type ClubFollow = {
  id: string;
  club_id: string;
  user_id: string;
  created_at?: string | null;
};

type ClubUpdate = {
  id: string;
  club_id: string;
  author_leader_id?: string | null;
  title: string;
  body: string;
  image_url?: string | null;
  is_published: boolean;
  created_at?: string | null;
};

type Profile = {
  id: string;
  display_name?: string | null;
  username?: string | null;
  bio?: string | null;
  location?: string | null;
  profile_image?: string | null;
  instagram_handle?: string | null;
  strava_link?: string | null;
  created_at?: string | null;
};

type LeaderDisplay = {
  id: string;
  user_id?: string | null;
  name: string;
  bio?: string | null;
  profile_image?: string | null;
  strava_link?: string | null;
  is_admin?: boolean | null;
  username?: string | null;
};

type FollowerDisplay = {
  id: string;
  user_id?: string | null;
  name: string;
  profile_image?: string | null;
  strava_link?: string | null;
  username?: string | null;
};

type AttendeeDisplay = {
  id: string;
  user_id?: string | null;
  name: string;
  profile_image?: string | null;
  strava_link?: string | null;
  attendance_count: number;
  username?: string | null;
};

const dayOrder = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const dayToIndex: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const defaultMarkerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

function formatTime(time?: string | null) {
  if (!time) return 'Time TBD';

  const trimmed = time.trim();

  if (/am|pm/i.test(trimmed)) return trimmed;

  const parts = trimmed.split(':');
  if (parts.length < 2) return trimmed;

  const hour = Number(parts[0]);
  const minute = parts[1];

  if (Number.isNaN(hour)) return trimmed;

  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 || 12;

  return `${normalizedHour}:${minute} ${suffix}`;
}

function formatConfirmedAgo(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function windowDates(weeks: number) {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const dates: string[] = [];
  for (let w = 0; w < weeks; w++) {
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday + w * 7);
    monday.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(toIsoDate(date));
    }
  }
  return dates;
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function formatDateLabel(date: Date) {
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function formatShortDate(dateString?: string | null) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getNextOccurrence(dayName: string, time?: string | null) {
  const now = new Date();
  const targetDay = dayToIndex[dayName];

  if (targetDay === undefined) return null;

  const next = new Date(now);
  const currentDay = now.getDay();
  let diff = targetDay - currentDay;

  if (diff < 0) diff += 7;

  next.setDate(now.getDate() + diff);
  next.setHours(0, 0, 0, 0);

  if (time) {
    const match = time.match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      next.setHours(hours, minutes, 0, 0);
    }
  }

  if (diff === 0 && next < now) {
    next.setDate(next.getDate() + 7);
  }

  return next;
}

function getNextRun(runs: Run[]): NextRunResult | null {
  const now = new Date();

  const upcoming = runs
    .map((run) => {
      if (run.is_recurring === false && run.run_date) {
        // One-off: use the specific date, skip if already passed
        const nextDate = new Date(run.run_date + 'T00:00:00');
        if (run.start_time) {
          const match = run.start_time.match(/^(\d{1,2}):(\d{2})/);
          if (match) nextDate.setHours(Number(match[1]), Number(match[2]), 0, 0);
        }
        if (nextDate < now) return null;
        return { run, nextDate };
      }
      // Recurring: find next occurrence by day of week
      const nextDate = getNextOccurrence(run.day_of_week, run.start_time);
      if (!nextDate) return null;
      return { run, nextDate };
    })
    .filter(Boolean) as NextRunResult[];

  if (!upcoming.length) return null;

  upcoming.sort((a, b) => a.nextDate.getTime() - b.nextDate.getTime());
  return upcoming[0];
}

function getTerrainType(run?: Run | null) {
  if (!run) return 'Terrain TBD';

  const vibes = run.vibes || [];
  const notes = (run.notes || '').toLowerCase();

  if (vibes.includes('Trail') || notes.includes('trail')) return 'Trail';
  if (notes.includes('road')) return 'Road';
  if (notes.includes('mixed')) return 'Mixed';

  return 'Road / Mixed';
}

function getVibeText(run?: Run | null) {
  if (!run?.vibes?.length) return 'All runners welcome';
  return run.vibes.slice(0, 4).join(' • ');
}

function initialsFromName(name: string) {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function getProfileRoute(profile: { username?: string | null; id?: string | null }) {
  if (profile.username) return `/profile/${profile.username}`;
  if (profile.id) return `/profile/${profile.id}`;
  return '#';
}

function runsByDay(runs: Run[]) {
  return dayOrder.reduce<Record<string, Run[]>>((acc, day) => {
    acc[day] = runs
      .filter((run) => run.day_of_week === day)
      .sort((a, b) => {
        const aTime = a.start_time || '';
        const bTime = b.start_time || '';
        return aTime.localeCompare(bTime);
      });
    return acc;
  }, {});
}

function firstLineAddress(address?: string | null) {
  if (!address) return 'Location coming soon';
  const parts = address.split(',').map((p) => p.trim());
  const street = parts[0];
  const city = parts[1] ?? null;
  const zipMatch = address.match(/\b\d{5}(-\d{4})?\b/);
  const zip = zipMatch ? zipMatch[0] : null;
  if (!city) return street;
  if (zip) return `${street}, ${city}, ${zip}`;
  return `${street}, ${city}`;
}

function clubInstagramUrl(handle?: string | null) {
  if (!handle) return null;
  const clean = handle.replace('@', '').trim();
  if (!clean) return null;
  return `https://www.instagram.com/${clean}/`;
}

function InstagramIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm0 2h10c1.66 0 3 1.34 3 3v10c0 1.66-1.34 3-3 3H7c-1.66 0-3-1.34-3-3V7c0-1.66 1.34-3 3-3zm5 2.5A5.5 5.5 0 1 0 17.5 12 5.5 5.5 0 0 0 12 6.5zm0 2A3.5 3.5 0 1 1 8.5 12 3.5 3.5 0 0 1 12 8.5zm4.75-2.25a1.25 1.25 0 1 0 1.25 1.25 1.25 1.25 0 0 0-1.25-1.25z" />
    </svg>
  );
}

function FacebookIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M22 12a10 10 0 1 0-11.5 9.88v-6.99H7.9V12h2.6V9.8c0-2.56 1.52-3.98 3.86-3.98 1.12 0 2.28.2 2.28.2v2.5h-1.28c-1.26 0-1.65.78-1.65 1.58V12h2.8l-.45 2.89h-2.35v6.99A10 10 0 0 0 22 12z" />
    </svg>
  );
}

function StravaIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M10.6 2 6.8 9.5h2.95L10.6 8l2.2 4.35H9.95L14.2 20l4.25-7.65H15.5L10.6 2Z" />
    </svg>
  );
}

function Avatar({
  name,
  image,
  size = 'md',
}: {
  name: string;
  image?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClass =
    size === 'sm'
      ? 'h-10 w-10 text-sm'
      : size === 'lg'
      ? 'h-16 w-16 text-lg'
      : 'h-12 w-12 text-base';

  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className={`${sizeClass} rounded-2xl border border-white/40 bg-white/90 object-cover shadow-sm`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} flex items-center justify-center rounded-2xl border border-white/40 bg-black text-white font-semibold shadow-sm`}
    >
      {initialsFromName(name)}
    </div>
  );
}

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-4">
      <h2 className="text-2xl font-semibold tracking-tight text-black">
        {title}
      </h2>
      {description ? (
        <p className="mt-2 text-sm leading-6 text-black/65">{description}</p>
      ) : null}
    </div>
  );
}

function LeaderLinkCard({ leader }: { leader: LeaderDisplay }) {
  const profileHref = leader.user_id
    ? getProfileRoute({ id: leader.user_id, username: leader.username })
    : undefined;

  const content = (
    <div className="flex items-center gap-3 rounded-2xl border border-black/8 bg-white/80 p-3 shadow-sm transition hover:bg-white">
      <Avatar name={leader.name} image={leader.profile_image} size="md" />
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-black">{leader.name}</p>
        {leader.bio ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-black/55">{leader.bio}</p>
        ) : null}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-black/55">
          {leader.strava_link ? (
            <a
              href={leader.strava_link}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-black/10 bg-white px-2.5 py-1 font-medium text-black/70 transition hover:bg-black hover:text-white"
              onClick={(e) => e.stopPropagation()}
            >
              Strava
            </a>
          ) : null}
          {leader.is_admin ? (
            <span className="rounded-full border border-black/10 bg-[#fff7cc] px-2.5 py-1 font-medium text-black/65">
              Admin
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (profileHref) {
    return <Link to={profileHref}>{content}</Link>;
  }

  return content;
}

function ClaimClubSection({ clubId, leaders }: { clubId: string; leaders: LeaderDisplay[] }) {
  const { user, leader } = useAuth();
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'already'>('idle');

  if (!user) return null;
  if (leaders.length > 0) return null;
  if (leader && leaders.some((l) => l.id === leader.id)) return null;

  async function handleClaim() {
    setStatus('loading');
    const { error } = await supabase.from('club_ownership_claims').insert({
      club_id: clubId,
      user_id: user!.id,
      leader_id: leader?.id ?? null,
    });
    if (error?.code === '23505') setStatus('already');
    else if (error) setStatus('idle');
    else setStatus('sent');
  }

  if (status === 'sent' || status === 'already') {
    return (
      <p className="mt-3 text-xs text-black/50">Claim request sent — awaiting admin approval.</p>
    );
  }

  return (
    <button
      onClick={handleClaim}
      disabled={status === 'loading'}
      className="mt-3 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-medium text-black/70 transition hover:bg-black hover:text-white disabled:opacity-50"
    >
      {status === 'loading' ? 'Sending…' : 'Claim this club'}
    </button>
  );
}

function HeroWithIntegratedLeaders({
  club,
  nextRun,
  leaders,
  canEdit,
}: {
  club: Club;
  nextRun: NextRunResult | null;
  leaders: LeaderDisplay[];
  canEdit: boolean;
}) {
  const instagramUrl = clubInstagramUrl(club.instagram_handle);

  return (
    <section className="mx-auto max-w-6xl px-4 pt-4 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-[32px] border border-black/10 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.08)]">
        <div className="relative h-[340px] w-full overflow-hidden bg-[#f7f7f5] sm:h-[400px] md:h-[470px]">
          {club.hero_image ? (
            <img
              src={club.hero_image}
              alt={club.name}
              className="h-full w-full object-cover"
              style={{ objectPosition: 'center center' }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#fff7cc] via-white to-[#f6e8fb]">
              <span className="text-sm font-medium text-black/40">No hero image yet</span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-white/10" />

          {canEdit ? (
            <div className="absolute right-4 top-4 z-10">
              <Link
                to={`/clubs/${club.slug}/edit`}
                className="inline-flex items-center rounded-full border border-white/40 bg-white/20 px-3 py-2 text-xs font-medium text-white shadow-sm backdrop-blur-md transition hover:bg-white/30"
              >
                Edit Club
              </Link>
            </div>
          ) : null}
        </div>

        <div className="relative border-t border-black/8 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-4 py-5 sm:px-6 sm:py-6">
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-4">
                <Avatar name={club.name} image={club.logo} size="lg" />
                <div className="min-w-0">
                  <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
                    {club.name}
                  </h1>
                  <p className="mt-2 text-sm text-black/70">
                    📍 {firstLineAddress(club.location)}
                  </p>
                  {club.description ? (
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-black/65">
                      {club.description}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-5">
                <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-black/45">
                  Run Leaders
                </h3>

                {leaders.length ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {leaders.map((leader) => (
                      <LeaderLinkCard key={leader.id} leader={leader} />
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 rounded-2xl border border-dashed border-black/10 bg-white/70 p-4 text-sm text-black/60">
                    No run leaders added yet.
                  </div>
                )}
                <ClaimClubSection clubId={club.id} leaders={leaders} />

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <FollowButton clubId={club.id} />

                  {instagramUrl ? (
                    <a
                      href={instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 transition hover:bg-gradient-to-br hover:from-pink-500 hover:to-yellow-400 hover:text-white"
                      aria-label="Instagram"
                      title="Instagram"
                    >
                      <InstagramIcon className="h-4 w-4 transition group-hover:scale-110" />
                    </a>
                  ) : null}

                  {club.facebook_link ? (
                    <a
                      href={club.facebook_link}
                      target="_blank"
                      rel="noreferrer"
                      className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 transition hover:bg-blue-600 hover:text-white"
                      aria-label="Facebook"
                      title="Facebook"
                    >
                      <FacebookIcon className="h-4 w-4 transition group-hover:scale-110" />
                    </a>
                  ) : null}

                  {club.strava_link ? (
                    <a
                      href={club.strava_link}
                      target="_blank"
                      rel="noreferrer"
                      className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 transition hover:bg-orange-500 hover:text-white"
                      aria-label="Strava"
                      title="Strava"
                    >
                      <StravaIcon className="h-4 w-4 transition group-hover:scale-110" />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="w-full lg:max-w-[360px]">
              <div className="rounded-3xl border border-black/10 bg-white/85 p-5 shadow-sm backdrop-blur">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-black">
                    {nextRun?.run.title || 'Next Run'}
                  </h2>
                  {nextRun ? (
                    <span className="rounded-full bg-[#ffde59] px-2.5 py-1 text-xs font-semibold text-black">
                      Upcoming
                    </span>
                  ) : null}
                </div>

                {nextRun ? (
                  <div className="space-y-2.5">
                    <p className="text-lg font-semibold text-black">
                      {formatDateLabel(nextRun.nextDate)}
                    </p>
                    <p className="text-sm text-black/75">🕒 {formatTime(nextRun.run.start_time)}</p>
                    <p className="text-sm text-black/75">
                      📍 {firstLineAddress(nextRun.run.location_address || club.location) || 'Location TBD'}
                    </p>
                    <p className="text-sm text-black/75">
                      🏃 {nextRun.run.distance || 'Distance TBD'}
                    </p>
                    <p className="text-sm text-black/75">🌄 {getTerrainType(nextRun.run)}</p>
                    <p className="text-sm text-black/75">✨ {getVibeText(nextRun.run)}</p>

                    <div className="pt-1">
                      <RsvpButton
                        runId={nextRun.run.id}
                        runDate={toIsoDate(nextRun.nextDate)}
                        clubId={club.slug}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-black/60">No upcoming run has been added yet.</p>
                )}

                {canEdit && nextRun && (
                  <div className="mt-4 border-t border-black/8 pt-4">
                    <Link
                      to={`/clubs/${club.slug}/confirm-attendance?runId=${nextRun.run.id}&date=${toIsoDate(nextRun.nextDate)}`}
                      className="inline-flex rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                    >
                      Confirm Attendance
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EqualHeightPanel({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`h-full rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm sm:p-6 ${className}`}
    >
      {children}
    </section>
  );
}

function InstagramHighlight({
  instagramHandle,
}: {
  instagramHandle?: string | null;
}) {
  return (
    <EqualHeightPanel>
      <SectionHeader
        title="Latest"
        description="Recent club activity and energy from Instagram."
      />

      {instagramHandle ? (
        <div className="overflow-hidden rounded-2xl border border-black/10 bg-white">
          <iframe
            title="Instagram club embed"
            src={`https://www.instagram.com/${instagramHandle}/embed`}
            className="h-[480px] w-full bg-white sm:h-[400px]"
            style={{ maxHeight: '480px' }}
          />
        </div>
      ) : (
        <div className="flex min-h-[480px] items-center justify-center rounded-2xl border border-dashed border-black/12 bg-white/75 p-5 text-center text-sm text-black/60 sm:min-h-[400px]">
          No Instagram account connected yet.
        </div>
      )}
    </EqualHeightPanel>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _UpdatesSection({
  updates,
  leaderNamesById,
}: {
  updates: ClubUpdate[];
  leaderNamesById: Record<string, string>;
}) {
  return (
    <EqualHeightPanel>
      <SectionHeader
        title="Updates"
        description="Posts, reminders, and club announcements from leaders."
      />

      {updates.length ? (
        <div className="max-h-[430px] space-y-4 overflow-y-auto pr-1 sm:max-h-[460px]">
          {updates.map((update) => {
            const authorName = update.author_leader_id
              ? leaderNamesById[update.author_leader_id]
              : undefined;

            return (
              <article
                key={update.id}
                className="overflow-hidden rounded-2xl border border-black/8 bg-white shadow-sm"
              >
                {update.image_url ? (
                  <img
                    src={update.image_url}
                    alt={update.title}
                    className="h-44 w-full object-cover"
                  />
                ) : null}

                <div className="p-4">
                  <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-black/50">
                    <span>{formatShortDate(update.created_at)}</span>
                    {authorName ? <span>• by {authorName}</span> : null}
                  </div>

                  <h3 className="text-base font-semibold text-black">{update.title}</h3>

                  <p className="mt-2 whitespace-pre-line text-sm leading-6 text-black/70">
                    {update.body}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="flex min-h-[430px] items-center justify-center rounded-2xl border border-dashed border-black/12 bg-white/75 p-5 text-center text-sm text-black/60 sm:min-h-[460px]">
          No updates have been posted yet.
        </div>
      )}
    </EqualHeightPanel>
  );
}

function WeekTimelineSchedule({
  runs,
  routesById,
  clubId,
  clubUuid,
  isLeader,
  locationKey,
}: {
  runs: Run[];
  routesById: Record<string, RouteRecord>;
  clubId: string;
  clubUuid: string;
  isLeader: boolean;
  locationKey: string;
}) {
  const recurringRuns = runs.filter((r) => r.is_recurring !== false);
  const oneOffRuns = runs.filter((r) => r.is_recurring === false);
  const groupedRuns = runsByDay(recurringRuns);
  const hasAnyRuns = runs.length > 0;
  const [weekOffset, setWeekOffset] = useState(0);
  const [confirmedDates, setConfirmedDates] = useState<Set<string>>(new Set());
  // Exceptions keyed `${run_id}:${run_date}` for the 4-week window.
  const [exceptions, setExceptions] = useState<Map<string, RunException>>(new Map());
  const [lastConfirmedAt, setLastConfirmedAt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  useEffect(() => {
    const latest = runs
      .map((r) => r.last_confirmed_at)
      .filter((v): v is string => !!v)
      .sort()
      .pop();
    setLastConfirmedAt(latest ?? null);
  }, [runs]);

  useEffect(() => {
    if (runs.length === 0) return;

    async function fetchExceptions() {
      const { data } = await supabase
        .from('run_instances')
        .select(
          'run_id, run_date, status, title, start_time_override, location_address_override, distance_override, notes_override, route_id'
        )
        .in('run_id', runs.map((r) => r.id))
        .in('run_date', windowDates(4));

      const next = new Map<string, RunException>();
      ((data || []) as RunException[]).forEach((row) => {
        next.set(`${row.run_id}:${row.run_date}`, row);
      });
      setExceptions(next);
    }

    fetchExceptions();
  }, [runs, locationKey]);

  async function handleConfirmSchedule() {
    setConfirming(true);
    setConfirmError(null);
    const { error } = await supabase.rpc('confirm_club_schedule', { p_club_id: clubUuid });
    if (error) {
      setConfirmError(error.message);
    } else {
      setLastConfirmedAt(new Date().toISOString());
    }
    setConfirming(false);
  }

  useEffect(() => {
    if (!isLeader || runs.length === 0) return;

    async function fetchConfirmed() {
      const runIds = runs.map((r) => r.id);
      // All dates across 4 weeks (this week + next 3)
      const allDates = windowDates(4);

      const { data } = await supabase
        .from('run_attendances')
        .select('run_id, run_date')
        .in('run_id', runIds)
        .in('run_date', allDates);

      if (data) {
        setConfirmedDates(
          new Set(
            (data as { run_id: string; run_date: string }[]).map(
              (row) => `${row.run_id}:${row.run_date}`
            )
          )
        );
      }
    }

    fetchConfirmed();
  }, [isLeader, runs, locationKey]);

  // Compute this week's Monday offset by weekOffset weeks
  const { weekDateByDay, weekLabel } = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const label = weekOffset === 0 ? 'This week' : weekOffset === 1 ? 'Next week' : `${fmt(monday)} – ${fmt(sunday)}`;

    const byDay = dayOrder.reduce<Record<string, Date>>((acc, day, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      acc[day] = date;
      return acc;
    }, {});

    return { weekDateByDay: byDay, weekLabel: label };
  }, [weekOffset]);

  if (!hasAnyRuns) {
    return (
      <div className="rounded-2xl border border-dashed border-black/12 bg-white/80 p-5 text-sm text-black/60">
        No weekly schedule has been added yet.
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-hidden rounded-[28px] border border-black/8 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-3 sm:p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setWeekOffset((w) => Math.max(w - 1, 0))}
          disabled={weekOffset === 0}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white text-sm text-black/60 transition hover:bg-black hover:text-white disabled:opacity-25"
        >
          ‹
        </button>
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-black/45">
          {weekLabel}
        </span>
        <button
          type="button"
          onClick={() => setWeekOffset((w) => Math.min(w + 1, 3))}
          disabled={weekOffset === 3}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white text-sm text-black/60 transition hover:bg-black hover:text-white disabled:opacity-25"
        >
          ›
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-black/8 bg-white/80 px-3 py-2">
        <p className="text-xs text-black/60">
          {lastConfirmedAt ? (
            <>
              <span className="font-semibold text-black/75">Schedule confirmed</span> by the club{' '}
              {formatConfirmedAgo(lastConfirmedAt)}
            </>
          ) : (
            <>Schedule not yet confirmed by the club</>
          )}
        </p>
        {isLeader && (
          <button
            type="button"
            onClick={handleConfirmSchedule}
            disabled={confirming}
            className="rounded-full border border-black/10 bg-black px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-black/80 disabled:opacity-50"
          >
            {confirming ? 'Confirming…' : 'Confirm this week\u2019s schedule'}
          </button>
        )}
        {confirmError && <p className="w-full text-xs text-red-600">{confirmError}</p>}
      </div>

      <div className="w-full min-w-0 space-y-3">
        {dayOrder.map((day) => {
          const dayDate = weekDateByDay[day];
          const dayDateIso = dayDate ? toIsoDate(dayDate) : null;
          // Merge recurring runs for this day with any one-off runs falling on this exact date
          const dayRuns = [
            ...(groupedRuns[day] || []),
            ...oneOffRuns.filter((r) => r.run_date === dayDateIso),
          ];
          const dateLabel = dayDate
            ? dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : null;

          return (
            <div
              key={day}
              className="grid w-full grid-cols-[80px_1fr] gap-3 sm:grid-cols-[100px_1fr] sm:gap-4"
            >
              <div className="pt-2">
                <div className="rounded-2xl border border-black/8 bg-white px-3 py-2 text-center shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-black/40">
                    {day.slice(0, 3)}
                  </p>
                  {dateLabel && (
                    <p className="mt-0.5 text-[11px] font-medium text-black/60">
                      {dateLabel}
                    </p>
                  )}
                </div>
              </div>

              <div className="relative min-w-0">
                <div className="absolute bottom-0 left-3 top-0 w-px bg-black/10" />

                <div className="min-w-0 space-y-3">
                  {dayRuns.length > 0 ? (
                    dayRuns.map((baseRun) => {
                      const baseDate =
                        baseRun.is_recurring === false
                          ? (baseRun.run_date ?? null)
                          : weekDateByDay[baseRun.day_of_week]
                          ? toIsoDate(weekDateByDay[baseRun.day_of_week])
                          : null;
                      const exception = baseDate
                        ? exceptions.get(`${baseRun.id}:${baseDate}`) ?? null
                        : null;

                      if (exception?.status === 'canceled') {
                        return (
                          <div key={baseRun.id} className="relative min-w-0 pl-8">
                            <div className="absolute left-[7px] top-5 h-3 w-3 rounded-full border-2 border-white bg-black/25 shadow-sm" />
                            <div className="rounded-2xl border border-dashed border-black/12 bg-white/70 px-4 py-3">
                              <span className="rounded-full bg-black/8 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-black/55">
                                Canceled this week
                              </span>
                              <p className="mt-1.5 text-sm text-black/45 line-through">
                                {baseRun.title || `${formatTime(baseRun.start_time)} run`}
                              </p>
                              {exception.notes_override && (
                                <p className="mt-1 text-xs text-black/55">{exception.notes_override}</p>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Layer the exception's overrides onto the standing slot for this date.
                      const run: Run = exception
                        ? {
                            ...baseRun,
                            title: exception.title ?? baseRun.title,
                            start_time: exception.start_time_override || baseRun.start_time,
                            location_address:
                              exception.location_address_override || baseRun.location_address,
                            distance: exception.distance_override || baseRun.distance,
                            notes: exception.notes_override || baseRun.notes,
                            route_id: exception.route_id || baseRun.route_id,
                          }
                        : baseRun;

                      const linkedRoute =
                        run.route_id && routesById[run.route_id]
                          ? routesById[run.route_id]
                          : null;

                      const routeHref = run.route_url || linkedRoute?.external_url || null;
                      const runDate = baseDate;

                      return (
                        <div key={run.id} className="relative min-w-0 pl-8">
                          <div className="absolute left-[7px] top-5 h-3 w-3 rounded-full border-2 border-white bg-black shadow-sm" />

                          <div className={
                            exception
                              ? 'min-w-0 overflow-hidden rounded-2xl border border-amber-300 bg-white shadow-sm'
                              : 'min-w-0 overflow-hidden rounded-2xl border border-black/8 bg-white shadow-sm'
                          }>
                            {/* Run details — top half */}
                            <div className="px-4 pt-3 pb-3">
                              {exception && (
                                <span className="mb-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-800">
                                  {exception.status === 'moved' ? 'Changed this week' : 'Special run'}
                                </span>
                              )}
                              {run.title && (
                                <p className="mb-2 text-sm font-semibold text-black">
                                  {run.title}
                                </p>
                              )}
                              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-black/35">Time</p>
                                  <p className="mt-0.5 text-sm font-semibold text-black">{formatTime(run.start_time)}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-black/35">Distance</p>
                                  <p className="mt-0.5 text-sm text-black/75">{run.distance || 'TBD'}</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-black/35">Location</p>
                                  <p className="mt-0.5 text-sm text-black/75">{firstLineAddress(run.location_address) || 'TBD'}</p>
                                </div>
                              </div>
                              {exception?.notes_override && (
                                <p className="mt-2 text-xs text-black/60">{exception.notes_override}</p>
                              )}

                              {run.vibes?.length ? (
                                <div className="mt-2.5 flex flex-wrap gap-1.5">
                                  {run.vibes.slice(0, 4).map((vibe) => (
                                    <span
                                      key={vibe}
                                      className="rounded-full border border-black/10 bg-[#fff7cc] px-2.5 py-0.5 text-[11px] font-medium text-black/65"
                                    >
                                      {vibe}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>

                            {/* CTAs — bottom half */}
                            {(routeHref || runDate || (isLeader && runDate)) && (
                              <div className="border-t border-black/6 px-4 py-3 space-y-2">
                                {routeHref && (
                                  <a
                                    href={routeHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex w-full items-center justify-center rounded-xl border border-black/10 bg-white py-2 text-xs font-medium text-black/70 transition hover:bg-black hover:text-white"
                                  >
                                    View Route ↗
                                  </a>
                                )}

                                {runDate && (
                                  <RsvpButton
                                    runId={run.id}
                                    runDate={runDate}
                                    clubId={clubId}
                                    fullWidth
                                  />
                                )}

                                {isLeader && runDate && (
                                  <Link
                                    to={`/clubs/${clubId}/confirm-attendance?runId=${run.id}&date=${runDate}`}
                                    className={
                                      confirmedDates.has(`${run.id}:${runDate}`)
                                        ? 'flex w-full items-center justify-center rounded-xl bg-pink-500 py-2 text-xs font-semibold text-white transition hover:bg-pink-600'
                                        : 'flex w-full items-center justify-center rounded-xl border border-pink-200 bg-pink-50 py-2 text-xs font-medium text-pink-700 transition hover:bg-pink-100'
                                    }
                                  >
                                    {confirmedDates.has(`${run.id}:${runDate}`)
                                      ? 'Confirmed ✓'
                                      : 'Confirm Attendance'}
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="relative pl-8">
                      <div className="absolute left-[8px] top-4 h-2.5 w-2.5 rounded-full bg-black/15" />
                      <div className="rounded-2xl border border-dashed border-black/10 bg-white/70 px-4 py-3 text-sm text-black/35">
                        No run scheduled
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type LeaderInvite = {
  id: string;
  email: string;
  invited_name: string | null;
  status: 'pending' | 'claimed' | 'canceled' | 'expired';
};

function AssignLeaderPanel({ clubId }: { clubId: string }) {
  const [invites, setInvites] = useState<LeaderInvite[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchInvites = async () => {
    const { data } = await supabase
      .from('club_leader_invites')
      .select('id, email, invited_name, status')
      .eq('club_id', clubId)
      .order('created_at', { ascending: false });
    setInvites((data || []) as LeaderInvite[]);
  };

  useEffect(() => { fetchInvites(); }, [clubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) { setFormError('Email is required.'); return; }

    const exists = invites.some(
      (inv) => inv.email.toLowerCase() === trimmedEmail && inv.status === 'pending'
    );
    if (exists) { setFormError('A pending invite already exists for this email.'); return; }

    setSaving(true);
    const { error } = await supabase.from('club_leader_invites').insert({
      club_id: clubId,
      email: trimmedEmail,
      invited_name: name.trim() || null,
      status: 'pending',
    });
    setSaving(false);

    if (error) { setFormError('Failed to send invite. Please try again.'); return; }
    setEmail('');
    setName('');
    fetchInvites();
  };

  const handleCancel = async (inviteId: string) => {
    await supabase
      .from('club_leader_invites')
      .update({ status: 'canceled' })
      .eq('id', inviteId);
    fetchInvites();
  };

  const statusLabel: Record<LeaderInvite['status'], string> = {
    pending: 'Pending',
    claimed: 'Claimed ✓',
    canceled: 'Canceled',
    expired: 'Expired',
  };

  const statusColor: Record<LeaderInvite['status'], string> = {
    pending: 'bg-[#fff7cc] text-black/70',
    claimed: 'bg-[#dcfce7] text-green-800',
    canceled: 'bg-black/5 text-black/40',
    expired: 'bg-black/5 text-black/40',
  };

  return (
    <div className="mt-5 rounded-2xl border border-pink-200 bg-pink-50 p-5">
      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-pink-700">
        Assign Club Leader
      </h3>
      <p className="mt-1 text-xs text-black/55">
        Enter the email of the person you want to lead this club. They'll be automatically assigned when they sign in.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="leader@example.com"
            className="flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-pink-300"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            className="flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-black placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-pink-300"
          />
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center rounded-xl bg-pink-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-pink-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Assign'}
          </button>
        </div>
        {formError && <p className="text-xs text-red-600">{formError}</p>}
      </form>

      {invites.length > 0 && (
        <div className="mt-4 space-y-2">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-black/8 bg-white px-4 py-2.5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-black">{invite.email}</p>
                {invite.invited_name && (
                  <p className="text-xs text-black/45">{invite.invited_name}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor[invite.status]}`}>
                  {statusLabel[invite.status]}
                </span>
                {invite.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => handleCancel(invite.id)}
                    className="text-xs text-black/35 transition hover:text-red-500"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClubDetails({
  club,
  runs,
  routesById,
  isLeader,
  isAdmin,
  locationKey,
}: {
  club: Club;
  runs: Run[];
  routesById: Record<string, RouteRecord>;
  isLeader: boolean;
  isAdmin: boolean;
  locationKey: string;
}) {
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const mapsHref =
    club.latitude != null && club.longitude != null
      ? `https://www.google.com/maps?q=${club.latitude},${club.longitude}`
      : club.location
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          club.location
        )}`
      : '#';

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: club.name,
          text: `Check out ${club.name} on Run Collective`,
          url: shareUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      window.alert('Club link copied to clipboard');
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  return (
    <section className="w-full overflow-x-hidden rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm sm:p-6">
      <h2 className="mb-4 text-2xl font-semibold tracking-tight text-black">
        Club Details
      </h2>

      <div className="grid min-w-0 gap-5 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="min-w-0 overflow-hidden">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-black/45">
            Run Schedule
          </h3>
          <WeekTimelineSchedule runs={runs} routesById={routesById} clubId={club.slug} clubUuid={club.id} isLeader={isLeader} locationKey={locationKey} />
        </div>

        <div className="min-w-0 space-y-4">
          <div className="rounded-2xl border border-black/8 bg-white/80 p-4 shadow-sm">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-black/45">
              Share Club
            </h3>

            <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="rounded-2xl bg-white p-3 shadow-sm">
                <QRCode
                  value={shareUrl || `https://runcollective.club/clubs/${club.slug}`}
                  size={120}
                />
              </div>

              <div className="w-full sm:max-w-[220px]">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex min-h-[48px] w-full items-center justify-center rounded-2xl bg-[#ffde59] px-5 py-3 text-sm font-semibold text-black transition hover:translate-y-[-1px]"
                >
                  Share Club
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-black/8 bg-white/80 p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-black/45">
              Meet Location
            </h3>

            {club.latitude != null && club.longitude != null ? (
              <div className="overflow-hidden rounded-2xl border border-black/10">
                <MapContainer
                  center={[club.latitude, club.longitude]}
                  zoom={13}
                  scrollWheelZoom={false}
                  style={{ height: '280px', width: '100%' }}
                >
                  <TileLayer attribution={CARTO_ATTRIBUTION} url={CARTO_TILE_URL} />
                  <Marker
                    position={[club.latitude, club.longitude]}
                    icon={defaultMarkerIcon}
                  >
                    <Popup>{club.name}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-black/12 bg-white p-5 text-sm text-black/60">
                Map coordinates have not been added for this club yet.
              </div>
            )}

            <div className="mt-3">
              <a
                href={mapsHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-black hover:text-white"
              >
                Open in Maps
              </a>
            </div>

            {isAdmin && <AssignLeaderPanel clubId={club.id} />}
          </div>
        </div>
      </div>
    </section>
  );
}

function FollowersSection({ followers }: { followers: FollowerDisplay[] }) {
  return (
    <section className="w-full min-w-0 overflow-x-hidden rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm sm:p-6">
      <SectionHeader title="Followers" description="Runners following this club." />

      {followers.length >= 10 ? (
        <div className="space-y-3">
          {followers.map((follower) => {
            const profileHref = follower.user_id
              ? getProfileRoute({
                  id: follower.user_id,
                  username: follower.username,
                })
              : undefined;

            return (
              <div
                key={follower.id}
                className="flex items-center justify-between gap-4 rounded-2xl border border-black/8 bg-white p-4 shadow-sm"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {profileHref ? (
                    <Link to={profileHref}>
                      <Avatar
                        name={follower.name}
                        image={follower.profile_image}
                        size="md"
                      />
                    </Link>
                  ) : (
                    <Avatar
                      name={follower.name}
                      image={follower.profile_image}
                      size="md"
                    />
                  )}

                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-black">
                      {profileHref ? (
                        <Link to={profileHref} className="hover:underline">
                          {follower.name}
                        </Link>
                      ) : (
                        follower.name
                      )}
                    </p>
                  </div>
                </div>

                {follower.strava_link ? (
                  <a
                    href={follower.strava_link}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/75 transition hover:bg-black hover:text-white"
                  >
                    Strava
                  </a>
                ) : (
                  <span className="shrink-0 text-xs text-black/35">—</span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex min-h-[120px] items-center justify-center rounded-2xl border border-dashed border-black/12 bg-white/75 p-5 text-center text-sm text-black/60">
          Be the first to follow this club.
        </div>
      )}
    </section>
  );
}

function AttendeesSection({ attendees }: { attendees: AttendeeDisplay[] }) {
  const totalAttendance = attendees.reduce(
    (sum, attendee) => sum + attendee.attendance_count,
    0
  );

  return (
    <section className="w-full min-w-0 overflow-x-hidden rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm sm:p-6">
      <SectionHeader
        title="Attendees"
        description="Attendance leaderboard for runners who show up consistently."
      />

      {attendees.length ? (
        <>
          <div className="mb-4 rounded-2xl border border-black/8 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/45">
              Total logged attendances
            </p>
            <p className="mt-2 text-3xl font-bold tracking-tight text-black">
              {totalAttendance}
            </p>
          </div>

          <div className="space-y-3">
            {attendees.map((attendee, index) => {
              const profileHref = attendee.user_id
                ? getProfileRoute({
                    id: attendee.user_id,
                    username: attendee.username,
                  })
                : undefined;

              return (
                <div
                  key={attendee.id}
                  className="flex items-center gap-4 rounded-2xl border border-black/8 bg-white p-4 shadow-sm"
                >
                  {/* Rank */}
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ffde59] text-xs font-bold text-black">
                    {index + 1}
                  </div>

                  {/* Avatar */}
                  <div className="shrink-0">
                    {profileHref ? (
                      <Link to={profileHref}>
                        <Avatar
                          name={attendee.name}
                          image={attendee.profile_image}
                          size="md"
                        />
                      </Link>
                    ) : (
                      <Avatar
                        name={attendee.name}
                        image={attendee.profile_image}
                        size="md"
                      />
                    )}
                  </div>

                  {/* Name + Strava */}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-black">
                      {profileHref ? (
                        <Link to={profileHref} className="hover:underline">
                          {attendee.name}
                        </Link>
                      ) : (
                        attendee.name
                      )}
                    </p>
                    {attendee.strava_link ? (
                      <a
                        href={attendee.strava_link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-black/55 transition hover:text-black"
                      >
                        <StravaIcon className="h-3 w-3" />
                        Strava
                      </a>
                    ) : null}
                  </div>

                  {/* Attendance count */}
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-black">
                      {attendee.attendance_count}
                    </p>
                    <p className="text-xs text-black/50">runs</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-black/12 bg-white/75 p-5 text-center text-sm text-black/60">
          Attendance data coming soon. This section will highlight runners who
          show up consistently.
        </div>
      )}
    </section>
  );
}

export default function ClubProfile() {
  const { id } = useParams();
  const { leader, canManageClub, loading: authLoading } = useAuth();
  const location = useLocation();

  const [club, setClub] = useState<Club | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [routesById, setRoutesById] = useState<Record<string, RouteRecord>>({});
  const [leaders, setLeaders] = useState<LeaderDisplay[]>([]);
  const [_updates, setUpdates] = useState<ClubUpdate[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [followers, setFollowers] = useState<FollowerDisplay[]>([]);
  const [attendees, setAttendees] = useState<AttendeeDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [redirectSlug, setRedirectSlug] = useState<string | null>(null);

  useEffect(() => {
    async function fetchClubData() {
      if (!id) return;

      setLoading(true);

      // If the URL param looks like a UUID (legacy link), redirect to slug URL
      if (UUID_REGEX.test(id)) {
        const { data: clubData } = await supabase
          .from('clubs')
          .select('slug')
          .eq('id', id)
          .single();

        if (clubData?.slug) {
          setRedirectSlug(clubData.slug);
        } else {
          setLoading(false);
        }
        return;
      }

      // Slug-based URL — fetch club by slug first
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .select('*')
        .eq('slug', id)
        .single();

      if (clubError) console.error('Error fetching club:', clubError);

      const resolvedClub = (clubData as Club | null) || null;
      const clubUuid = resolvedClub?.id ?? id;

      const [
        { data: runsData, error: runsError },
        { data: routesData, error: routesError },
        { data: followsData, error: followsError },
        { data: updatesData, error: updatesError },
      ] = await Promise.all([
        supabase.from('runs').select('*').eq('club_id', clubUuid).eq('is_active', true),
        supabase.from('routes').select('id, name, external_url').eq('club_id', clubUuid),
        supabase.from('club_follows').select('*').eq('club_id', clubUuid),
        supabase
          .from('club_updates')
          .select('*')
          .eq('club_id', clubUuid)
          .eq('is_published', true)
          .order('created_at', { ascending: false }),
      ]);

      if (runsError) console.error('Error fetching runs:', runsError);
      if (routesError) console.error('Error fetching routes:', routesError);
      if (followsError) console.error('Error fetching follows:', followsError);
      if (updatesError) console.error('Error fetching updates:', updatesError);
      const resolvedRuns = (runsData as Run[]) || [];
      const resolvedRoutes = (routesData as RouteRecord[]) || [];
      const resolvedFollows = (followsData as ClubFollow[]) || [];
      const resolvedUpdates = (updatesData as ClubUpdate[]) || [];

      setClub(resolvedClub);
      setRuns(resolvedRuns);
      setUpdates(resolvedUpdates);

      // Track page view (fire-and-forget)
      if (resolvedClub?.id) {
        supabase.from('club_page_views').insert({ club_id: resolvedClub.id }).then(() => {});
      }

      const routeMap = resolvedRoutes.reduce<Record<string, RouteRecord>>(
        (acc, route) => {
          acc[route.id] = route;
          return acc;
        },
        {}
      );
      setRoutesById(routeMap);

      const followerUserIds = resolvedFollows
        .map((follow) => follow.user_id)
        .filter(Boolean);

      const updateLeaderIds = resolvedUpdates
        .map((update) => update.author_leader_id)
        .filter(Boolean) as string[];

      let clubLeaderIds: string[] = [];
      if (resolvedClub?.id) {
        const { data: clData } = await supabase
          .from('club_leaders')
          .select('leader_id')
          .eq('club_id', resolvedClub.id);
        clubLeaderIds = ((clData || []) as { leader_id: string }[]).map((r) => r.leader_id);
      }

      const uniqueLeaderIds = Array.from(
        new Set(
          [...updateLeaderIds, ...clubLeaderIds, resolvedClub?.leader_id].filter(
            Boolean
          ) as string[]
        )
      );

      let resolvedRunLeaders: RunLeader[] = [];
      if (uniqueLeaderIds.length) {
        // Public-safe RPC: leader name/photo/admin flag without exposing
        // run_leaders.email, which RLS keeps private to the leader themselves.
        const { data: leadersData, error: leadersError } = await supabase.rpc(
          'get_run_leader_profiles',
          { p_ids: uniqueLeaderIds }
        );

        if (leadersError) {
          console.error('Error fetching run leaders:', leadersError);
        } else {
          resolvedRunLeaders = (leadersData as RunLeader[]) || [];
        }
      }

      const leaderUserIds = resolvedRunLeaders
        .map((entry) => entry.user_id)
        .filter(Boolean) as string[];

      const allProfileIds = Array.from(
        new Set([...followerUserIds, ...leaderUserIds].filter(Boolean))
      );

      let profilesById: Record<string, Profile> = {};
      if (allProfileIds.length) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .in('id', allProfileIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        } else {
          const profiles = (profilesData as Profile[]) || [];
          profilesById = profiles.reduce<Record<string, Profile>>((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
      }

      const leaderDisplays: LeaderDisplay[] = resolvedRunLeaders.map((entry) => {
        const profile = entry.user_id ? profilesById[entry.user_id] : undefined;

        return {
          id: entry.id,
          user_id: entry.user_id || null,
          name: profile?.display_name || entry.name,
          bio: profile?.bio || null,
          profile_image: profile?.profile_image || entry.profile_image || null,
          strava_link: profile?.strava_link || null,
          is_admin: entry.is_admin || false,
          username: profile?.username || null,
        };
      });

      setLeaders(leaderDisplays);

      const followersDisplay: FollowerDisplay[] = resolvedFollows.map((follow) => {
        const profile = profilesById[follow.user_id];

        return {
          id: follow.id,
          user_id: follow.user_id,
          name:
            profile?.display_name ||
            profile?.username ||
            'Run Collective Member',
          profile_image: profile?.profile_image || null,
          strava_link: profile?.strava_link || null,
          username: profile?.username || null,
        };
      });

      setFollowers(followersDisplay);

      // Attendance leaderboard. Raw run_attendances rows are private to the
      // attendee and the club's leaders — who was present at which run on which
      // date is not public — so the standings come from an aggregate RPC.
      const { data: leaderboardData, error: leaderboardError } = await supabase.rpc(
        'get_club_attendance_leaderboard',
        { p_club_id: clubUuid }
      );

      if (leaderboardError) {
        console.error('Error fetching attendance leaderboard:', leaderboardError);
        setAttendees([]);
      } else {
        const leaderboardRows = (leaderboardData || []) as {
          user_id: string;
          name: string | null;
          profile_image: string | null;
          strava_link: string | null;
          username: string | null;
          attendance_count: number | string;
        }[];

        const attendeeDisplays: AttendeeDisplay[] = leaderboardRows.map((row) => ({
          id: row.user_id,
          user_id: row.user_id,
          name: row.name || row.username || 'Run Collective Member',
          profile_image: row.profile_image || null,
          strava_link: row.strava_link || null,
          attendance_count: Number(row.attendance_count) || 0,
          username: row.username || null,
        }));

        setAttendees(attendeeDisplays);
      }

      setLoading(false);
    }

    fetchClubData();
  }, [id]);

  const nextRun = useMemo(() => getNextRun(runs), [runs]);

  const _leaderNamesById = useMemo(() => { // eslint-disable-line @typescript-eslint/no-unused-vars
    return leaders.reduce<Record<string, string>>((acc, entry) => {
      acc[entry.id] = entry.name;
      return acc;
    }, {});
  }, [leaders]);

  const canEdit = useMemo(() => {
    if (!club || !leader) return false;
    return canManageClub(club.id);
  }, [club, leader, canManageClub]);

  if (redirectSlug) {
    return <Navigate to={`/clubs/${redirectSlug}`} replace />;
  }

  if (loading || authLoading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-5">
            <div className="h-[760px] rounded-[32px] bg-black/5" />
            <div className="h-[520px] rounded-3xl bg-black/5" />
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="h-[520px] rounded-3xl bg-black/5" />
              <div className="h-[520px] rounded-3xl bg-black/5" />
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="h-[360px] rounded-3xl bg-black/5" />
              <div className="h-[360px] rounded-3xl bg-black/5" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!club) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-black">Club not found</h1>
          <p className="mt-3 text-black/65">
            We couldn’t find the club you’re looking for.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <HeroWithIntegratedLeaders
        club={club}
        nextRun={nextRun}
        leaders={leaders}
        canEdit={canEdit}
      />

      <div className="mx-auto mt-5 max-w-6xl overflow-x-hidden px-4 sm:px-6 lg:px-8">
        <ClubDetails club={club} runs={runs} routesById={routesById} isLeader={canEdit} isAdmin={leader?.is_admin ?? false} locationKey={location.pathname + location.search} />

        <div className="mt-5">
          <InstagramHighlight instagramHandle={club.instagram_handle} />
          {/* <UpdatesSection updates={updates} leaderNamesById={leaderNamesById} /> */}
        </div>

        <div className="mt-5 space-y-5">
          <FollowersSection followers={followers} />
          <AttendeesSection attendees={attendees} />
        </div>
      </div>
    </main>
  );
}